import useStore from "stores/index";
import { formatMsgDisplayTime, getMessagesForThreadKey } from "stores/chatStore";
import { classroomActiveInfoV2 } from "js/utils/classroomSessionUtils";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import shallow from "zustand/shallow";
import { getClassroomIdForChat } from "../../../../utils/chatUtils";
import ChatInput from "../ChatInput";
import MessageItem from "../MessageItem";
import NoEntries from "../NoEntries";
import { useUserSessionStore } from "stores/v2/userSessionStore";

const MessagePane = ({ username, classroom, threadKey, chatBlocked, avatarColors, isEmbedded, handleChatEnable }) => {
	const [messages, setMessages] = useState([]);
	const [inputMessage, setInputMessage] = useState("");
	const [disabledChat, setDisabledChat] = useState(chatBlocked);
	const [classActive, setClassActive] = useState(false);
	const [showOneWayChatNotice, setOneWayChatNotice] = useState(false);
	let isMonitoringReminderShown = false;

	const scrollRef = React.useRef(null);
	const conversationContainerRef = React.useRef(null);

	// This is only used for sending and fetching messages
	const classroomIdForChat = useMemo(() => getClassroomIdForChat(classroom), [classroom]);

	const classroomDetails = useStore.getState().classroom.getClassroomV2(classroom.sourceId);

	const handleNewMessagesReceived = () => {
		const newMessages = getMessagesForThreadKey(useStore.getState(), threadKey);
		setMessages(newMessages);

		const filteredMsgs = newMessages.filter((msg) => !msg.isFakeMessage);

		// do not scroll if there are no messages are all
		if (!filteredMsgs.length) {
			return;
		}

		// auto scroll to bottom text only if not user initiated load by scrolling to top
		if (!useStore.getState().chat.scrollFetch && scrollRef.current) {
			setTimeout(() => {
				if (scrollRef && scrollRef.current) {
					scrollRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
				}
			}, 1000);
			return;
		}

		if (conversationContainerRef.current) {
			const scrollValue = conversationContainerRef.current.scrollHeight / 4;
			conversationContainerRef.current.scrollTop = scrollValue;
		}
		useStore.getState().chat.toggleScrollFetch(false);
	};

	useEffect(() => {
		setDisabledChat(chatBlocked);
	}, [chatBlocked]);

	// Scroll down to bottom when window is opened
	useEffect(() => {
		if (scrollRef && scrollRef.current) {
			setTimeout(() => {
				scrollRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
			}, 1000);
		}
	}, []);

	useEffect(() => {
		handleNewWindowData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (classroomDetails && Object.entries(classroomDetails).length) {
			const [isActiveClass] = classroomActiveInfoV2(classroomDetails);
			setClassActive(isActiveClass);
		}
	}, [classroomDetails]);

	useEffect(() => {
		setMessages("");

		let unsubMessages = () => null;
		let unsubActiveWindowData = () => null;

		const getMessages = async () => {
			await useStore.getState().chat.fetchMessages(
				{
					classroomId: classroomIdForChat,
					threadKey,
				},
				isEmbedded
			);

			const newThread = useStore.getState().chat.threads.data[threadKey];
			if (newThread) {
				// subscription wont kick in new message handler if thread has already been fetched and has no changes
				handleNewMessagesReceived();
				handleNewWindowData();
			}

			unsubMessages = useStore.subscribe(
				(state) => state.chat.threads.data[threadKey],
				(messages) => handleNewMessagesReceived(messages),
				shallow
			);

			unsubActiveWindowData = useStore.subscribe(
				(state) => state.activeClassroomWindowStore,
				() => handleNewWindowData(),
				shallow
			);
		};

		getMessages();

		return () => {
			unsubMessages();
			unsubActiveWindowData();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [threadKey]);

	const handleNewWindowData = async () => {
		const deviceStatus = await useUserSessionStore.getState().getDeviceStats();

		// Do not show notice if device is offline
		if (deviceStatus && !deviceStatus["last_status"]) {
			if (showOneWayChatNotice) {
				setOneWayChatNotice(false);
			}
			return;
		}

		const selectedStudentName = useStore.getState().chat.selectedStudentName;
		const isStudentAgentClientOnly = useStore.getState().activeClassroomWindow.IsUserPlatformAgentOnly(selectedStudentName);
		setOneWayChatNotice(isStudentAgentClientOnly);
	};

	const sendMessage = (messageToResend) => {
		if (messageToResend) {
			useStore.getState().chat.sendMessage(
				{
					...messageToResend,
					username,
				},
				false
			);
			return;
		}

		if (!inputMessage || !inputMessage.trim() || inputMessage.length >= 1000) {
			return;
		}

		let participants = [];

		if (classroomDetails && classroomDetails.owners) {
			participants = [...classroomDetails.owners];
		}

		const id = useStore.getState().chat.generateMessageId();
		useStore.getState().chat.sendMessage({
			message: inputMessage,
			username,
			classroomId: classroomIdForChat,
			threadKey,
			id,
			participants,
		});

		setInputMessage("");
	};

	const handleMessageSend = () => {
		sendMessage();
	};

	const handleKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			handleMessageSend();
		}
	};

	const handleOnChange = (event) => {
		setInputMessage(event.target.value);
	};

	const handleOnScroll = (event) => {
		if (event.target.scrollTop === 0) {
			useStore.getState().chat.toggleScrollFetch(true);
			useStore.getState().chat.fetchMessages({ classroomId: classroomIdForChat, threadKey });
		}
	};

	const getAvatarColor = (message, classroom) => {
		if (!classroom || !classroom.owners) {
			return null;
		}

		const teachers = classroom.owners.filter((owner) => owner !== localStorage.getItem("USERNAME"));
		const colorIndex = teachers.findIndex((teacher) => teacher === message.sender.username);

		if (colorIndex !== -1) {
			return avatarColors[colorIndex + 1];
		}

		return null;
	};

	if (
		useStore.getState().chat.threads.isFetching &&
		!useStore.getState().chat.scrollFetch &&
		!useStore.getState().chat.threads.isUpdateFetch
	) {
		// TODO: replace with proper loading states
		return <NoEntries title="Loading messages" key="loading" />;
	}

	const renderedMessages = [];
	const monitoringReminder = (
		<div key="monitoringReminder" className="ClassroomChatMessagePane_monitoringReminder">
			For your protection, this chat service is logged and monitored by your school district.
		</div>
	);

	if (messages) {
		const filteredMessages = messages.filter((message) => !message.isFakeMessage);
		let formattedMessages = filteredMessages;

		filteredMessages.forEach((message, index) => {
			let avatarColor = null;
			const isOwner = message.sender.username === localStorage.getItem("USERNAME");

			formattedMessages = formatMsgTs(index, filteredMessages);

			if (!isOwner) {
				const isStudent = message.sender.username === username;
				avatarColor = isStudent ? avatarColors[0] : getAvatarColor(message, classroomDetails);
			}

			if (index > 0 && !isMonitoringReminderShown) {
				const date1 = new Date(filteredMessages[index - 1].timestamp);
				const date2 = new Date(filteredMessages[index].timestamp);
				if (date1.getDay() > date2.getDay() || date1.getMonth() > date2.getMonth() || date1.getYear() > date2.getYear()) {
					renderedMessages.push(monitoringReminder);
					isMonitoringReminderShown = true;
				}
			}

			renderedMessages.push(
				<MessageItem
					avatarColor={avatarColor}
					key={formattedMessages[index].id}
					message={formattedMessages[index]}
					resendMessage={sendMessage}
					isOwner={isOwner}
				/>
			);
		});
	}

	if (!renderedMessages.length) {
		renderedMessages.push(
			<NoEntries title="No Messages yet" key="no-messages">
				{monitoringReminder}
			</NoEntries>
		);
	}

	if (showOneWayChatNotice) {
		renderedMessages.unshift(
			<div className="ClassroomChatMessagePane_oneWayChatNotice">
				<div>Student cannot reply on their device</div>
				<div>
					Chat will only be one-way. &nbsp;
					<a
						className="ClassroomChatMessagePane_oneWayChatLearnMore"
						href="https://docs.classwize.net/help/classwize-chat#the-student-can-see-my-messages-but-they-can%E2%80%99t-reply-to-them"
						target="_blank"
						rel="noopener noreferrer"
					>
						Learn more
					</a>
				</div>
			</div>
		);
	}

	return (
		<div className="ClassroomChatMessagePane_container">
			<div className="ClassroomChatMessagePane_conversationContainer" onScroll={handleOnScroll} ref={conversationContainerRef}>
				{renderedMessages.reverse()}
				<div ref={scrollRef} />
			</div>
			<ChatInput
				classActive={classActive}
				disabledChat={disabledChat}
				handleOnChange={handleOnChange}
				handleKeyDown={handleKeyDown}
				inputMessage={inputMessage}
				handleMessageSend={handleMessageSend}
				chatError={useStore.getState().chat.chatError}
				handleChatEnable={handleChatEnable}
			/>
		</div>
	);
};

MessagePane.propTypes = {
	username: PropTypes.string,
	classroom: PropTypes.object,
	threadKey: PropTypes.string,
	chatBlocked: PropTypes.bool,
	avatarColors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	isEmbedded: PropTypes.bool,
	handleChatEnable: PropTypes.func,
};

export default MessagePane;

// Format message to show timestamp / avatar
function formatMsgTs(index, msgs) {
	const today = new Date();

	if (index === 0) {
		msgs[index].showAvatar = true;
	}

	if (index === msgs.length - 1) {
		msgs[index].renderTs = formatMsgDisplayTime(today, msgs[index].timestamp);
		return msgs;
	}

	if (msgs[index].sender.username !== msgs[index + 1].sender.username) {
		msgs[index].renderTs = formatMsgDisplayTime(today, msgs[index].timestamp);
		msgs[index + 1].showAvatar = true;
		return msgs;
	}

	const startDate = new Date(msgs[index].timestamp);
	const nextDate = new Date(msgs[index + 1].timestamp);
	const diffInMinutes = Math.floor((startDate.getTime() - nextDate.getTime()) / 60000);
	// if current date and the next one is within 15 minutes of each other
	const showTs = diffInMinutes > 15;
	msgs[index].renderTs = showTs ? formatMsgDisplayTime(today, msgs[index].timestamp) : null;
	msgs[index + 1].showAvatar = showTs;

	return msgs;
}
